import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  GeneratedAssignment,
  GeneratedAssignmentAttempt,
  GeneratedAssignmentStatus,
  GeneratedUpdateAssignmentAttempt,
} from '@repo/backend-api-spec';
import { Err, ExtractErr, Ok, Result } from '@gametailors/web-core/classes';
import { UnexpectedStateError } from '@gametailors/web-core/errors';
import { AssignmentApiService } from '../services';

@Component({ template: '' })
export abstract class Assignment<T extends GeneratedAssignment = GeneratedAssignment> implements OnInit {
  public assignmentUid = signal<string | null>(null);
  public assignment = signal<T | null>(null);

  public attemptUid = signal<string | null>(null);
  public attempt = signal<GeneratedAssignmentAttempt | null>(null);

  protected abstract router: Router;
  protected abstract activatedRoute: ActivatedRoute;
  protected abstract assignmentApi: AssignmentApiService;

  async ngOnInit() {
    // Assignment query param
    this.assignmentUid.set(this.activatedRoute.snapshot.queryParams['assignmentUid']);

    const assignmentUid = this.assignmentUid();
    if (!assignmentUid!) {
      console.error('assignmentUid query parameter not found');
      await this.redirectHome();
      return;
    }

    // Assignment section
    const assignment = await this.assignmentApi.getAssignment<T>(assignmentUid);

    if (assignment.isErr()) {
      console.error(assignment.unwrapErr());
      await this.redirectHome();
      return;
    }

    this.assignment.set(assignment.unwrap());
    console.debug('Assignment updated', this.assignment());

    // Assignment attempt section
    const attempt = await this.createAttempt();

    if (attempt.isErr()) {
      await this.redirectHome();
      return;
    }

    this.attempt.set(attempt.unwrap());
    this.attemptUid.set(attempt.unwrap().uid);
    console.debug('Created assignment attempt: ', this.attempt());
  }

  public async redirectHome() {
    return await this.router.navigate(['/']);
  }

  public async quitAssignment() {
    await Promise.all([
      this.redirectHome(),
      this.updateAttempt({
        status: GeneratedAssignmentStatus.Canceled,
      }),
    ]);
  }

  /**
   * Patch the current assignment attempt with the provided data.
   */
  public async updateAttempt(
    data: GeneratedUpdateAssignmentAttempt,
  ): Promise<Result<GeneratedAssignmentAttempt, UnexpectedStateError | ExtractErr<typeof this.assignmentApi.updateAssignmentAttempt>>> {
    const attemptUid = this.attemptUid();
    if (!attemptUid) return Err(new UnexpectedStateError('Expected assignment attempt uid to exist'));

    const attempt = await this.assignmentApi.updateAssignmentAttempt(attemptUid, data);

    if (attempt.isOk()) {
      this.attempt.set(attempt.unwrap());
      return Ok(attempt.unwrap());
    }

    return Err(attempt.unwrapErr());
  }

  private async createAttempt(): Promise<
    Result<GeneratedAssignmentAttempt, UnexpectedStateError | ExtractErr<typeof this.assignmentApi.createAssignmentAttempt>>
  > {
    const assignmentUid = this.assignmentUid();
    if (!assignmentUid) return Err(new UnexpectedStateError('Expected assignment uid to exist'));

    const attempt = await this.assignmentApi.createAssignmentAttempt(assignmentUid, {
      status: 'IN_PROGRESS',
      items: [],
    });

    if (attempt.isOk()) return Ok(attempt.unwrap());
    else return Err(attempt.unwrapErr());
  }
}
